// Оболочка (агент shell): вкладки ленты, «Главная» (буфер, правка, шрифт, абзац, стили СТО), «Файл» (сохранение,
// версии, экспорт, печать, Word), «Вид» (режимы, масштаб, страницы, тема), «Справка»; поиск и замена, командная
// палитра, «/»-меню, ручка блоков, мини-панель выделения, непечатаемые знаки, автоформат, вёрстка страниц,
// строка состояния, резервная копия при сбоях сохранения, контекстное меню.
import { Extension } from '@tiptap/core';
import type { RegistryApi } from '../../core/registry';
import { appStore } from '../../core/store';
import { uiStore } from '../../ui/uiState';
import { PromptDialog, ConfirmDialog } from '../../ui/prompts';
import { CreateProjectDialog } from '../../ui/StartScreen';
import { setupCommands } from './commands';
import { setupFile } from './file';
import { setupHelp } from './help';
import { setupPreview } from './preview';
import { setupStatus } from './status';
import { setupBackup } from './backup';
import { setupPosition } from './position';
import { FindExtension, openFind, findStep, pushSpec, closeFind } from './find';
import { SlashExtension } from './slash';
import { MarksExtension, FocusBlockExtension, syncMarks } from './marks';
import { AutoformatExtension } from './autoformat';
import { installGlobalKeys } from './keys';
import './shell.css';

export default function setup(r: RegistryApi) {
  // --- вкладки (идентификаторы - общий контракт: file, home, insert, formulas, refs, sto, view)
  r.ribbonTabs.add({ id: 'file', title: 'Файл', order: 0 });
  r.ribbonTabs.add({ id: 'home', title: 'Главная', order: 10 });
  r.ribbonTabs.add({ id: 'insert', title: 'Вставка', order: 20 });
  r.ribbonTabs.add({ id: 'formulas', title: 'Формулы', order: 30 });
  r.ribbonTabs.add({ id: 'refs', title: 'Ссылки', order: 40 });
  r.ribbonTabs.add({ id: 'sto', title: 'СТО', order: 50 });
  r.ribbonTabs.add({ id: 'view', title: 'Вид', order: 60 });

  setupCommands(r);
  setupFile(r);
  setupHelp(r);
  setupPreview(r);
  setupStatus(r);
  setupBackup(r);
  setupPosition(r);

  // --- поиск и замена
  r.commands.add({ id: 'edit.find', title: 'Найти', description: 'Поиск по документу (регистр, слово целиком, регулярные выражения)',
    icon: 'search', shortcut: 'Mod-f', run: () => openFind(false) });
  r.commands.add({ id: 'edit.replace', title: 'Заменить', description: 'Поиск и замена по документу', icon: 'replace', shortcut: 'Mod-h',
    run: () => openFind(true) });
  r.commands.add({ id: 'edit.findNext', title: 'Найти далее', icon: 'down', shortcut: 'F3', run: (c) => { if (!uiStore.getState().find.open) openFind(false); else findStep(c.editor, 1); } });
  r.commands.add({ id: 'edit.findPrev', title: 'Найти ранее', icon: 'chevron-up', shortcut: 'Shift-F3', run: (c) => findStep(c.editor, -1) });

  // --- расширения редактора
  r.extensions.add({ id: 'shell.find', order: 50, create: () => FindExtension });
  r.extensions.add({ id: 'shell.slash', order: 51, create: () => SlashExtension });
  r.extensions.add({ id: 'shell.marks', order: 52, create: () => [MarksExtension, FocusBlockExtension] });
  r.extensions.add({ id: 'shell.autoformat', order: 53, create: () => AutoformatExtension });
  // Esc в тексте: закрыть поиск, затем выйти из «Фокуса» (раньше прочих обработчиков Esc)
  r.extensions.add({ id: 'shell.escape', order: 54, create: () => Extension.create({
    name: 'stoShellEscape',
    priority: 1100,
    addKeyboardShortcuts() {
      return {
        Escape: () => {
          if (document.querySelector('.slash-menu, .popover')) return false;
          const u = uiStore.getState();
          if (u.find.open) { closeFind(); return true; }
          if (u.focusMode) { u.set({ focusMode: false }); return true; }
          return false;
        },
      };
    },
  }) });

  // --- свои окна вместо prompt()/confirm()
  r.dialogs.add({ id: 'ui.prompt', component: PromptDialog });
  r.dialogs.add({ id: 'ui.confirm', component: ConfirmDialog });
  // «Новый отчёт»: простое окно; функция sto_intel может заменить его мастером (тот же id, результат - id отчёта)
  r.dialogs.add({ id: 'project.create', component: CreateProjectDialog });

  // --- контекстное меню: буфер обмена и блок (другие функции добавляют свои группы)
  r.contextMenu.add({ id: 'shell.clipboard', order: 0, items: (c, t) => [
    { command: 'edit.cut', shortcut: 'Mod-x', disabled: t.selectionEmpty },
    { command: 'edit.copy', shortcut: 'Mod-c', disabled: t.selectionEmpty },
    { command: 'edit.paste', shortcut: 'Mod-v' },
    { command: 'edit.pasteText', shortcut: 'Mod-Shift-v' },
    ...(c.editor && !t.selectionEmpty ? [{ command: 'format.clear' }] : []),
  ] });
  r.contextMenu.add({ id: 'shell.block', order: 90, items: (_c, t) => {
    const top = t.nodes.find((n) => n.type !== 'appendix');
    if (!top) return null;
    const textual = ['paragraph', 'heading', 'stoList'].includes(top.type);
    return [
      ...(textual ? [{ label: 'Стиль', icon: 'paragraph', children: [
        { command: 'block.paragraph' }, { command: 'block.h1' }, { command: 'block.h2' }, { command: 'block.h3' },
        { separator: true }, { command: 'list.dash' }, { command: 'list.letter' }, { command: 'list.digit' },
      ] }] : []),
      { command: 'block.moveUp' }, { command: 'block.moveDown' }, { command: 'block.duplicate' },
    ];
  } });

  // --- вид: синхронизация плагинов с настройками
  uiStore.subscribe((s, p) => {
    if (s.showMarks !== p.showMarks || s.focusMode !== p.focusMode) syncMarks(appStore.getState().editor);
    if (s.find.open !== p.find.open && !s.find.open) pushSpec(appStore.getState().editor);
  });
  r.events.on('editor:created', (ed) => {
    syncMarks(ed);
    if (uiStore.getState().find.open) pushSpec(ed);
    const w = window as any;
    if (w.sto && !w.sto.ui) w.sto.ui = uiStore;          // для отладки и автотестов: window.sto.ui.getState()
  });
  r.events.on('project:closed', () => uiStore.getState().set({ focusMode: false, backstage: null, find: { ...uiStore.getState().find, open: false } }));

  installGlobalKeys();
}
